import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../helpers/utils"

const buttonVariants = cva(
  `
    inline-flex items-center justify-center rounded-lg text-sm font-bold whitespace-nowrap transition-colors
    focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden
    disabled:pointer-events-none disabled:opacity-50
    3xl:text-lg
  `,
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm group-hover:bg-primary/90 hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs group-hover:bg-destructive/90 hover:bg-destructive/90",
        outline: `
          border border-primary bg-background text-primary shadow-xs
          group-hover:bg-accent group-hover:text-accent-foreground
          hover:bg-accent hover:text-accent-foreground
        `,
        outline_secondary: `
          border border-secondary-foreground bg-background text-secondary-foreground shadow-xs
          group-hover:bg-accent group-hover:text-accent-foreground
          hover:bg-accent hover:text-accent-foreground
        `,
        secondary: "bg-secondary text-secondary-foreground shadow-xs group-hover:bg-secondary/80 hover:bg-secondary/80",
        secondary_light: `
          bg-secondary/80 font-normal text-secondary-foreground shadow-xs
          group-hover:bg-secondary/60
          hover:bg-secondary/60
        `,
        ghost: "group-hover:bg-accent group-hover:text-accent-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 group-hover:underline hover:underline",
        white: "group-hover:bg-gray hover:bg-gray bg-white text-black shadow-sm",
        black: "group-hover:bg-gray hover:bg-gray bg-black text-white shadow-sm",
        basketNext: "w-full bg-primary py-0 font-normal text-white",
      },
      size: {
        default: "h-9 px-4 py-2",
        defaultNoGrow: "h-9 px-4 py-2 text-sm 3xl:text-sm",
        sm: "h-8 rounded-sm px-3 text-xs",
        smNoGrow: "h-8 px-3 text-xs 3xl:text-xs",
        tiny: "h-4 px-0 text-xs 3xl:text-xs",
        lg: "h-10 rounded-lg px-8",
        icon: "h-9 w-9 3xl:h-13.5 3xl:w-13.5",
        iconNoGrow: "h-9 w-9 3xl:h-9 3xl:w-9 3xl:text-sm",
        paginationLink: "h-9 w-auto",
        productCard: "h-9 px-4 py-2 3xl:text-base 3xl:font-normal",
        productCardIcon: "h-9 w-12",
        productCardIconSmall: "h-8 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
